/**
 * New invoice wizard (brief §5.2 / §5.3) — Customer → Days → Extras → Note → Review.
 * Every step change saves the server-side draft, so leaving half-way keeps it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, ChevronRight, ChevronLeft, Send, Download, Building2, Briefcase, Save, Eye } from 'lucide-react';
import { ScheduleDaysPicker } from '../../components/portal/invoice/ScheduleDaysPicker';
import { ExtraLinesEditor } from '../../components/portal/invoice/ExtraLinesEditor';
import { InvoiceTotals } from '../../components/portal/invoice/InvoiceTotals';
import { InvoicePreviewDialog } from '../../components/portal/invoice/InvoicePreviewDialog';
import { SavedCustomersCard } from '../../components/portal/SavedCustomersCard';
import { computeTotals, cisRateOf, gbp, typeLabel, ukDate } from '../../components/portal/invoice/invoiceUtils';
import { useCompanySplit, OtherCompanyNote, otherCompany } from '../../components/portal/invoice/CompanySplit';
import { jobCode } from '../../utils/jobCode';

const STEPS = ['Customer', 'Days', 'Extras', 'Note', 'Review'];

export default function PortalInvoiceCreate() {
  const { api, features } = useAuth();
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [draftId, setDraftId] = useState(invoiceId || null);
  const [profile, setProfile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [customerType, setCustomerType] = useState('lux');
  const [company, setCompany] = useState(params.get('company') === 'LC' ? 'LC' : 'LD');
  const [customer, setCustomer] = useState(null);
  const [period, setPeriod] = useState({ start: '', end: '' });
  const [labourLines, setLabourLines] = useState([]);
  const [extraLines, setExtraLines] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { split, reload: reloadSplit } = useCompanySplit(api, period, customerType === 'lux');

  useEffect(() => {
    (async () => {
      try {
        const [p, pr] = await Promise.all([api.get('/portal/contractor-profile'), api.get('/portal/invoices2/projects').catch(() => ({ data: [] }))]);
        setProfile(p.data); setProjects(pr.data || []);
        if (invoiceId) {
          const r = await api.get(`/portal/invoices2/${invoiceId}`);
          const inv = r.data;
          if (inv.status !== 'draft') { navigate(`/portal/invoice/${invoiceId}`, { replace: true }); return; }
          setCustomerType(inv.customer?.type || 'lux'); setCompany(inv.customer?.company || 'LD');
          if (inv.customer?.type === 'adhoc' && inv.customer?.customer_id) setCustomer({ id: inv.customer.customer_id, name: inv.customer.name, address: inv.customer.address, email: inv.customer.email });
          setPeriod({ start: inv.period_start || '', end: inv.period_end || '' });
          setLabourLines((inv.lines || []).filter(l => l.dates?.length).map(l => ({ ...l, all_dates: l.dates, already_invoiced: [] })));
          setExtraLines((inv.lines || []).filter(l => !l.dates?.length).map(l => ({ ...l, project_id: l.project_id || '', qty: l.qty ?? '', rate: l.rate ?? '', amount: l.amount ?? '' })));
          setNote(inv.note || ''); setStep(1);
        } else if (params.get('start') && params.get('end')) {
          setPeriod({ start: params.get('start'), end: params.get('end') });
        } else {
          const s = await api.get('/portal/invoices2/period-suggestion');
          setPeriod({ start: s.data.period_start, end: s.data.period_end });
        }
      } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Could not load'); }
      finally { setLoading(false); }
    })();
  }, [api, invoiceId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // One invoice per LUX company: switching company drops lines that belong to the other one.
  const pickCompany = (code) => {
    setCustomerType('lux');
    if (code !== company) {
      setCompany(code);
      setLabourLines([]);
      setExtraLines(ls => ls.filter(l => !l.project_id || (projects.find(p => p.id === l.project_id)?.company || code) === code));
    }
  };
  const companyProjects = useMemo(() => customerType === 'lux' ? projects.filter(p => !p.company || p.company === company) : projects, [projects, customerType, company]);
  const daysFor = (code) => (split?.companies || []).find(c => c.company === code)?.days ?? null;

  const allLines = useMemo(() => [...labourLines, ...extraLines].map(l => ({ ...l, amount: Number(l.amount) || 0 })), [labourLines, extraLines]);
  const totals = useMemo(() => computeTotals(allLines, cisRateOf(profile)), [allLines, profile]);

  const payload = useCallback(() => ({
    customer_type: customerType, company, customer_id: customer?.id || null,
    period_start: customerType === 'lux' ? period.start || null : null, period_end: customerType === 'lux' ? period.end || null : null,
    note: note || null,
    lines: allLines.map(l => ({ id: String(l.id).startsWith('tmp-') || String(l.id).startsWith('sched-') ? undefined : l.id, type: l.type, description: l.description || null, project_id: l.project_id || null, dates: l.dates || null, qty: l.qty === '' ? null : Number(l.qty), rate: l.rate === '' ? null : Number(l.rate), amount: Number(l.amount) || 0, cis_applies: !!l.cis_applies })),
  }), [customerType, company, customer, period, note, allLines]);

  const saveDraft = useCallback(async ({ successToast = false } = {}) => {
    try {
      const r = draftId ? await api.put(`/portal/invoices2/${draftId}`, payload()) : await api.post('/portal/invoices2/drafts', payload());
      if (!draftId) setDraftId(r.data.id);
      if (successToast) toast.success('Draft saved');
      return r.data;
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'object' ? det.message || 'Could not save draft' : det || 'Could not save draft');
      return null;
    }
  }, [api, draftId, payload]);

  const next = async () => {
    if (step === 0 && customerType === 'adhoc' && !customer) { toast.error('Pick or add a customer'); return; }
    if (!(await saveDraft())) return;
    setStep(s => Math.min(STEPS.length - 1, s + (step === 0 && customerType === 'adhoc' ? 2 : 1)));
  };
  const back = () => setStep(s => Math.max(0, s - (step === 2 && customerType === 'adhoc' ? 2 : 1)));

  const preview = async () => {
    setBusy(true);
    try { const d = await saveDraft(); if (d) setPreviewOpen(true); }
    finally { setBusy(false); }
  };

  const issue = async (send) => {
    setBusy(true);
    try {
      const d = await saveDraft();
      if (!d) return;
      const r = await api.post(`/portal/invoices2/${d.id}/issue`, { send });
      toast.success(`${r.data.invoice_number} issued${r.data.status === 'sent' ? ' and sent' : ' — saved to your Invoices'}`);
      // One invoice per company: if the other LUX company still has days this period, hand over to it.
      const other = customerType === 'lux' ? otherCompany(await reloadSplit(), company) : null;
      navigate(`/portal/invoice/${d.id}`, { replace: true, state: other ? { next: { ...other, start: period.start, end: period.end } } : undefined });
    } catch (e) {
      const det = e.response?.data?.detail;
      const LABELS = { business_name: 'business name', business_address: 'business address', utr_number: 'UTR', bank_account_name: 'account name', bank_sort_code: 'sort code', bank_account_number: 'account number' };
      toast.error(typeof det === 'object' ? `${det.message}: ${(det.missing || []).map(k => LABELS[k] || k).join(', ')}` : det || 'Could not issue', { duration: 8000, action: typeof det === 'object' ? { label: 'Open Profile', onClick: () => navigate('/portal/profile') } : undefined });
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>;

  return (
    <div className="p-4 space-y-4" data-testid="invoice-wizard">
      <ol className="flex items-center gap-1 text-[11px]" data-testid="wizard-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex-1 text-center py-1.5 rounded-lg ${i === step ? 'bg-[#F5A623] text-[#1E2530] font-bold' : i < step ? 'bg-white/10 text-gray-200' : 'bg-white/5 text-gray-500'} ${customerType === 'adhoc' && i === 1 ? 'opacity-30' : ''}`}>{s}</li>
        ))}
      </ol>

      {step === 0 && (
        <div className="space-y-3" data-testid="step-customer">
          <h2 className="text-white font-semibold">Who is this invoice to?</h2>
          <div className="grid grid-cols-2 gap-2">
            {[['LD', 'LUX Developments', 'Default'], ['LC', 'LUX Construction', '']].map(([code, name, hint]) => {
              const days = daysFor(code);
              return (
              <button key={code} type="button" onClick={() => pickCompany(code)}
                className={`rounded-xl border p-4 text-left min-h-[88px] active:scale-95 ${customerType === 'lux' && company === code ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/15 bg-[#2A3240]'}`} data-testid={`customer-lux-${code}`}>
                <Building2 className="w-5 h-5 text-[#F5A623] mb-2" />
                <div className="text-white font-semibold leading-tight">{name}</div>
                {days !== null ? (
                  <div className={`text-[11px] ${days > 0 ? 'text-[#F5A623]' : 'text-gray-500'}`} data-testid={`customer-lux-${code}-days`}>{days > 0 ? `${days} day${days === 1 ? '' : 's'} to invoice` : 'Nothing to invoice this period'}</div>
                ) : hint && <div className="text-[11px] text-gray-500">{hint}</div>}
              </button>
              );
            })}
          </div>
          {split?.both && (
            <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2.5" data-testid="both-companies-note">
              You've worked for both companies between {ukDate(period.start)} and {ukDate(period.end)} — that's <span className="font-semibold">two invoices, one for each</span>. Start with either; we'll prompt you for the other once it's issued.
            </div>
          )}
          <button type="button" onClick={() => setCustomerType('adhoc')} className={`w-full rounded-xl border p-4 text-left active:scale-95 flex items-center gap-3 ${customerType === 'adhoc' ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/15 bg-[#2A3240]'}`} data-testid="customer-adhoc">
            <Briefcase className="w-5 h-5 text-purple-300" />
            <div><div className="text-white font-semibold">Another company</div><div className="text-[11px] text-gray-500">Your own customer — private, not visible to LUX. Manual lines only.</div></div>
          </button>
          {customerType === 'adhoc' && (
            <div className="rounded-xl bg-[#2A3240] border border-white/10 p-3">
              {customer && <div className="mb-2 text-sm text-white" data-testid="customer-picked">Invoice to: <b>{customer.name}</b> <button type="button" className="text-[#F5A623] text-xs ml-2 underline" onClick={() => setCustomer(null)}>change</button></div>}
              {!customer && <SavedCustomersCard api={api} compact onPick={setCustomer} />}
            </div>
          )}
        </div>
      )}

      {step === 1 && customerType === 'lux' && (
        <div className="space-y-3" data-testid="step-days">
          <h2 className="text-white font-semibold">Days worked for {company === 'LC' ? 'LUX Construction' : 'LUX Developments'}</h2>
          <ScheduleDaysPicker api={api} company={company} period={period} setPeriod={setPeriod} labourLines={labourLines} setLabourLines={setLabourLines} />
          <OtherCompanyNote split={split} company={company} />
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="step-extras">
          <h2 className="text-white font-semibold">Anything extra?</h2>
          <ExtraLinesEditor lines={extraLines} setLines={setExtraLines} projects={companyProjects} showProject={customerType === 'lux'} />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3" data-testid="step-note">
          <h2 className="text-white font-semibold">Note on the invoice</h2>
          <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Prints on the PDF, e.g. “Includes Saturday 14th as agreed with Chris”" className="bg-[#2A3240] border-white/20 text-white min-h-[120px]" data-testid="invoice-note" />
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3" data-testid="step-review">
          <h2 className="text-white font-semibold">Review</h2>
          <div className="rounded-xl bg-[#2A3240] border border-white/10 p-3 text-sm text-gray-300 space-y-1">
            <div>To: <span className="text-white font-semibold">{customerType === 'lux' ? (company === 'LC' ? 'LUX Construction' : 'LUX Developments') : customer?.name}</span></div>
            {customerType === 'lux' && period.start && <div>Period: <span className="text-white">{ukDate(period.start)} – {ukDate(period.end)}</span></div>}
            <div>Number: <span className="text-white">{profile?.next_invoice_number}</span> <span className="text-gray-500">(assigned when issued)</span> · Template: <span className="text-white capitalize">{profile?.invoice_template || 'simple'}</span></div>
          </div>
          <div className="rounded-xl bg-[#2A3240] border border-white/10 divide-y divide-white/5" data-testid="review-lines">
            {allLines.length === 0 && <div className="p-4 text-sm text-gray-500 text-center">No lines yet — go back and pull days or add a line.</div>}
            {allLines.map((l, i) => (
              <div key={l.id || i} className="p-3 flex items-center gap-3 text-sm" data-testid={`review-line-${i}`}>
                <div className="w-16 shrink-0 text-[#F5A623] font-semibold text-xs">{jobCode((l.project_ref || l.project_code) ? l : projects.find(p => p.id === l.project_id), features) || '—'}</div>
                <div className="flex-1 min-w-0"><div className="text-white truncate">{l.description || typeLabel(l.type)}</div><div className="text-[11px] text-gray-500">{typeLabel(l.type)}{l.cis_applies ? ' · CIS' : ''}{l.qty ? ` · ${l.qty} × ${gbp(l.rate)}` : ''}</div></div>
                <div className="text-white font-semibold tabular-nums">{gbp(l.amount)}</div>
              </div>
            ))}
          </div>
          <InvoiceTotals totals={totals} />
          {note && <div className="text-xs text-gray-400 rounded-xl border border-white/10 p-3"><span className="text-gray-500">Note: </span>{note}</div>}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        {step > 0 && <Button variant="outline" onClick={back} className="h-12 border-white/20 text-white bg-white/5 px-4" data-testid="wizard-back"><ChevronLeft className="w-5 h-5" /></Button>}
        {step < STEPS.length - 1 ? (
          <Button onClick={next} className="flex-1 h-12 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="wizard-next">Next <ChevronRight className="w-5 h-5 ml-1" /></Button>
        ) : (
          <div className="flex-1 grid grid-cols-1 gap-2">
            <Button variant="outline" onClick={preview} disabled={busy || allLines.length === 0} className="h-12 border-[#F5A623]/50 text-[#F5A623] bg-[#F5A623]/10 hover:bg-[#F5A623]/20 font-semibold" data-testid="wizard-preview-pdf"><Eye className="w-4 h-4 mr-2" /> Preview PDF</Button>
            <Button onClick={() => issue(true)} disabled={busy || allLines.length === 0} className="h-12 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="wizard-issue-send">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Issue & send</>}</Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => issue(false)} disabled={busy || allLines.length === 0} className="h-11 border-white/20 text-white bg-white/5" data-testid="wizard-issue-only"><Download className="w-4 h-4 mr-1" /> Issue only</Button>
              <Button variant="outline" onClick={async () => { const d = await saveDraft({ successToast: true }); if (d) navigate('/portal/invoice'); }} disabled={busy} className="h-11 border-white/20 text-white bg-white/5" data-testid="wizard-save-draft"><Save className="w-4 h-4 mr-1" /> Save draft</Button>
            </div>
            <p className="text-[11px] text-gray-500 text-center">“Issue only” assigns the number and saves the invoice to your Invoices list without emailing it.</p>
          </div>
        )}
      </div>
      <InvoicePreviewDialog api={api} invoiceId={draftId} open={previewOpen} onClose={() => setPreviewOpen(false)} title={`Preview — ${profile?.next_invoice_number || 'draft'}`} />
    </div>
  );
}
